import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  ApprovalChain, ApprovalStage, ApprovalStageStatus,
  EntityChainStageInput,
  getEntityChain, getEntityStages, createEntityApprovalChain,
  approveStage, rejectStage, resetEntityStages, deleteEntityChain,
} from '../../services/approvalService';
import { logActivity } from '../../services/activityService';
import PeoplePicker, { Person } from '../PeoplePicker';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton, BM_LABEL, BM_INPUT, BM_TEXTAREA, BM_TITLE_INPUT,
} from '../BoardModal';

interface Props {
  pca: IPublicClientApplication;
  projectId?: string | null;
  entityType: number;          // ApprovalEntityType.Task / Milestone / RFP
  entityId: string;
  entityTitle: string;
  myOid: string;
  canManage: boolean;          // PM — can build/remove chains
  activityEntityType?: number | null;  // ActivityEntityType for logging; null = skip
  onChainState?: (s: { hasChain: boolean; requireCompletion: boolean; allApproved: boolean }) => void;
}

const STATUS = ApprovalStageStatus;
const isOverdue = (s: ApprovalStage) =>
  s.dagrun_pm_status === STATUS.Pending && !!s.dagrun_pm_deadline &&
  new Date(s.dagrun_pm_deadline).getTime() < Date.now();

export default function EntityApprovalSection({
  pca, projectId, entityType, entityId, entityTitle, myOid, canManage, activityEntityType, onChainState,
}: Props) {
  const [chain, setChain] = useState<ApprovalChain | null | undefined>(undefined); // undefined = loading
  const [stages, setStages] = useState<ApprovalStage[]>([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  const reload = useCallback(async () => {
    const [c, s] = await Promise.all([getEntityChain(pca, entityId), getEntityStages(pca, entityId)]);
    setChain(c);
    setStages(s.sort((a, b) => (a.dagrun_pm_stageorder ?? 0) - (b.dagrun_pm_stageorder ?? 0)));
  }, [pca, entityId]);
  useEffect(() => { reload().catch(() => setChain(null)); }, [reload]);

  const ordered = stages;
  const anyRejected = ordered.some(s => s.dagrun_pm_status === STATUS.Rejected);
  const allApproved = ordered.length > 0 && ordered.every(s => s.dagrun_pm_status === STATUS.Approved);
  const activeIndex = anyRejected ? -1 : ordered.findIndex(s => s.dagrun_pm_status === STATUS.Pending);
  const requireCompletion = !!chain?.dagrun_pm_requirecompletion;

  useEffect(() => {
    onChainState?.({ hasChain: !!chain, requireCompletion, allApproved });
  }, [chain, requireCompletion, allApproved, onChainState]);

  const log = (action: string, description: string) => {
    if (activityEntityType == null || !projectId) return;
    logActivity(pca, {
      projectId, entityType: activityEntityType, entityId, entityTitle,
      action, description,
    }).catch(() => {});
  };

  const doApprove = async (stage: ApprovalStage) => {
    setBusy(true);
    try {
      await approveStage(pca, stage.dagrun_pm_approvalstageid);
      log('ApprovalApproved', `${stage.dagrun_pm_approvername ?? 'Approver'} approved "${stage.dagrun_pm_name}"`);
      await reload();
    } finally { setBusy(false); }
  };
  const doReject = async (stage: ApprovalStage) => {
    if (!rejectComment.trim()) return;
    setBusy(true);
    try {
      await rejectStage(pca, stage.dagrun_pm_approvalstageid, rejectComment.trim());
      log('ApprovalRejected', `${stage.dagrun_pm_approvername ?? 'Approver'} rejected "${stage.dagrun_pm_name}": ${rejectComment.trim()}`);
      setRejectingId(null); setRejectComment('');
      await reload();
    } finally { setBusy(false); }
  };
  const doRestart = async () => {
    setBusy(true);
    try { await resetEntityStages(pca, entityId); log('ApprovalRestarted', 'Approval chain restarted'); await reload(); }
    finally { setBusy(false); }
  };
  const doRemove = async () => {
    setBusy(true);
    try { await deleteEntityChain(pca, entityId); setConfirmRemove(false); await reload(); }
    finally { setBusy(false); }
  };

  if (chain === undefined) return <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>;

  // ── No chain yet ──
  if (!chain) {
    return (
      <div>
        <div style={{ fontSize: 13, color: 'var(--ink-4)', marginBottom: canManage ? 8 : 0 }}>
          No approval chain on this item.
        </div>
        {canManage && (
          <button type="button" onClick={() => setShowBuilder(true)}
            style={{ background: 'transparent', color: '#0E7490', border: '1px solid #0E7490', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
            + Add Approval Chain
          </button>
        )}
        {showBuilder && (
          <ChainBuilderModal
            pca={pca} entityTitle={entityTitle}
            onClose={() => setShowBuilder(false)}
            onCreate={async (reqCompletion, chainStages) => {
              await createEntityApprovalChain(pca, {
                projectId, entityType, entityId, entityTitle,
                requireCompletion: reqCompletion, stages: chainStages,
              });
              log('ApprovalChainCreated', `Approval chain added (${chainStages.length} stage${chainStages.length === 1 ? '' : 's'})`);
              setShowBuilder(false);
              await reload();
            }}
          />
        )}
      </div>
    );
  }

  // ── Chain exists — timeline + actions ──
  return (
    <div>
      {/* Status banner */}
      {allApproved ? (
        <Banner bg="#D1FAE5" fg="#065F46">✓ All stages approved</Banner>
      ) : anyRejected ? (
        <Banner bg="#FEE2E2" fg="#991B1B">Rejected — restart to re-run the chain</Banner>
      ) : requireCompletion ? (
        <Banner bg="#FEF3C7" fg="#92400E">Approval required before this item can be completed</Banner>
      ) : null}

      {/* Horizontal stepper timeline */}
      <div style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto', padding: '6px 0 10px', gap: 0 }}>
        {ordered.map((s, i) => {
          const status = s.dagrun_pm_status ?? STATUS.Pending;
          const active = i === activeIndex;
          const overdue = isOverdue(s);
          const color =
            status === STATUS.Approved ? '#059669' :
            status === STATUS.Rejected ? '#DC2626' :
            active ? '#0E7490' : 'var(--ink-5)';
          return (
            <React.Fragment key={s.dagrun_pm_approvalstageid}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 92, flexShrink: 0 }}>
                <div title={s.dagrun_pm_instructions ?? ''} style={{
                  width: 28, height: 28, borderRadius: '50%',
                  border: `2px solid ${color}`,
                  background: status === STATUS.Approved ? color : active ? 'var(--brand-mist)' : '#fff',
                  color: status === STATUS.Approved ? '#fff' : color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700,
                }}>
                  {status === STATUS.Approved ? '✓' : status === STATUS.Rejected ? '✕' : i + 1}
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', marginTop: 6, textAlign: 'center', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.dagrun_pm_name}</div>
                <div style={{ fontSize: 10, color: 'var(--ink-4)', textAlign: 'center' }}>{s.dagrun_pm_approvername ?? '—'}</div>
                {s.dagrun_pm_deadline && (
                  <div style={{ fontSize: 10, color: overdue ? '#DC2626' : 'var(--ink-4)', fontWeight: overdue ? 700 : 400 }}>
                    {overdue ? 'Overdue ' : 'Due '}{new Date(s.dagrun_pm_deadline).toLocaleDateString()}
                  </div>
                )}
              </div>
              {i < ordered.length - 1 && (
                <div style={{ flex: '0 0 24px', height: 2, background: 'var(--border)', marginTop: 14 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Per-stage detail: active stage actions, decided info */}
      {ordered.map((s, i) => {
        const status = s.dagrun_pm_status ?? STATUS.Pending;
        const isActive = i === activeIndex;
        const mine = s.dagrun_pm_approverid === myOid;
        return (
          <div key={`d-${s.dagrun_pm_approvalstageid}`}
            style={{ padding: '8px 10px', borderRadius: 6, border: '0.5px solid var(--border-light)', marginBottom: 6, background: isActive ? 'var(--surface-2)' : 'transparent' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{i + 1}. {s.dagrun_pm_name}</span>
              <StatusPill status={status} />
            </div>
            {s.dagrun_pm_instructions && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, whiteSpace: 'pre-wrap' }}>{s.dagrun_pm_instructions}</div>}
            {s.dagrun_pm_comments && (status === STATUS.Rejected || status === STATUS.Approved) && (
              <div style={{ fontSize: 12, color: status === STATUS.Rejected ? '#991B1B' : 'var(--ink-3)', marginTop: 4 }}>
                “{s.dagrun_pm_comments}”
              </div>
            )}
            {isActive && mine && !anyRejected && (
              <div style={{ marginTop: 8 }}>
                {rejectingId === s.dagrun_pm_approvalstageid ? (
                  <div>
                    <textarea value={rejectComment} onChange={e => setRejectComment(e.target.value)}
                      placeholder="Reason for rejection (required)…" style={{ ...BM_TEXTAREA, minHeight: 54 }} />
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
                      <BMCancelButton onClick={() => { setRejectingId(null); setRejectComment(''); }} disabled={busy} />
                      <BMDangerButton onClick={() => doReject(s)} disabled={busy || !rejectComment.trim()}>Confirm reject</BMDangerButton>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" disabled={busy} onClick={() => doApprove(s)}
                      style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>Approve</button>
                    <button type="button" disabled={busy} onClick={() => setRejectingId(s.dagrun_pm_approvalstageid)}
                      style={{ background: 'transparent', color: '#DC2626', border: '1px solid #DC2626', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>Reject</button>
                  </div>
                )}
              </div>
            )}
            {isActive && !mine && !anyRejected && (
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 6 }}>Waiting on {s.dagrun_pm_approvername ?? 'the approver'}.</div>
            )}
          </div>
        );
      })}

      {/* Chain-level actions */}
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        {anyRejected && (canManage || ordered.some(s => s.dagrun_pm_approverid === myOid)) && (
          <button type="button" disabled={busy} onClick={doRestart}
            style={{ background: 'transparent', color: '#0E7490', border: '1px solid #0E7490', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
            Restart chain
          </button>
        )}
        {canManage && (
          <button type="button" disabled={busy} onClick={() => setConfirmRemove(true)}
            style={{ background: 'transparent', color: 'var(--ink-4)', border: 'none', padding: '5px 0', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
            Remove chain
          </button>
        )}
      </div>

      {confirmRemove && (
        <BoardModal open title="Remove approval chain" onClose={() => setConfirmRemove(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmRemove(false)} disabled={busy} />
            <BMDangerButton onClick={doRemove} disabled={busy}>Remove</BMDangerButton>
          </>}>
          Remove this approval chain and all its stages? This cannot be undone.
        </BoardModal>
      )}
    </div>
  );
}

function Banner({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return <div style={{ background: bg, color: fg, borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, marginBottom: 10 }}>{children}</div>;
}
function StatusPill({ status }: { status: number }) {
  const map: Record<number, { bg: string; fg: string; label: string }> = {
    [STATUS.Pending]:  { bg: '#F3F4F6', fg: '#5A6272', label: 'Pending' },
    [STATUS.Approved]: { bg: '#D1FAE5', fg: '#065F46', label: 'Approved' },
    [STATUS.Rejected]: { bg: '#FEE2E2', fg: '#991B1B', label: 'Rejected' },
    [STATUS.Skipped]:  { bg: '#F3F4F6', fg: '#9198A8', label: 'Skipped' },
  };
  const c = map[status] ?? map[STATUS.Pending];
  return <span style={{ background: c.bg, color: c.fg, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>{c.label}</span>;
}

// ── Chain builder modal (up to 5 stages) ───────────────────────────────
interface DraftStage { key: string; label: string; approver: Person | null; deadline: string; instructions: string }
let nextKey = 1;

function ChainBuilderModal({ pca, entityTitle, onClose, onCreate }: {
  pca: IPublicClientApplication;
  entityTitle: string;
  onClose: () => void;
  onCreate: (requireCompletion: boolean, stages: EntityChainStageInput[]) => Promise<void>;
}) {
  const [requireCompletion, setRequireCompletion] = useState(false);
  const [stages, setStages] = useState<DraftStage[]>([{ key: `s${nextKey++}`, label: '', approver: null, deadline: '', instructions: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addStage = () => { if (stages.length < 5) setStages(p => [...p, { key: `s${nextKey++}`, label: '', approver: null, deadline: '', instructions: '' }]); };
  const removeStage = (key: string) => setStages(p => p.length > 1 ? p.filter(s => s.key !== key) : p);
  const update = (key: string, patch: Partial<DraftStage>) => setStages(p => p.map(s => s.key === key ? { ...s, ...patch } : s));

  const valid = useMemo(() => stages.every(s => !!s.approver), [stages]);

  const submit = async () => {
    if (saving) return;
    if (!stages.every(s => s.approver)) { setError('Every stage needs an approver.'); return; }
    setSaving(true); setError(null);
    try {
      const input: EntityChainStageInput[] = stages.map((s, i) => ({
        label: s.label.trim() || `Stage ${i + 1}`,
        approverid: s.approver!.aadObjectId,
        approvername: s.approver!.displayName,
        deadline: s.deadline || null,
        instructions: s.instructions.trim() || null,
      }));
      await onCreate(requireCompletion, input);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the chain.');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Add Approval Chain" width={560} onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !valid}>{saving ? 'Creating…' : 'Create Chain'}</BMPrimaryButton>
      </>}>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>Approval for <strong>{entityTitle}</strong>. Up to 5 stages, approved in order.</div>

      {stages.map((s, i) => (
        <div key={s.key} style={{ border: '0.5px solid var(--border-light)', borderRadius: 8, padding: 12, marginBottom: 10, background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-3)' }}>Stage {i + 1}</span>
            <div style={{ flex: 1 }} />
            {stages.length > 1 && (
              <button type="button" onClick={() => removeStage(s.key)} aria-label="Remove stage"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 16 }}>×</button>
            )}
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={BM_LABEL}>Stage name</label>
            <input value={s.label} onChange={e => update(s.key, { label: e.target.value })} style={BM_TITLE_INPUT} placeholder={`e.g. Manager review`} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={BM_LABEL}>Approver *</label>
            <PeoplePicker pca={pca} value={s.approver} onChange={p => update(s.key, { approver: p })} placeholder="Select approver…" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={BM_LABEL}>Deadline (optional)</label>
              <input type="date" value={s.deadline} onChange={e => update(s.key, { deadline: e.target.value })} style={BM_INPUT} />
            </div>
          </div>
          <div>
            <label style={BM_LABEL}>Instructions (optional)</label>
            <textarea value={s.instructions} onChange={e => update(s.key, { instructions: e.target.value })} style={{ ...BM_TEXTAREA, minHeight: 48 }} />
          </div>
        </div>
      ))}

      {stages.length < 5 && (
        <button type="button" onClick={addStage}
          style={{ background: 'transparent', color: '#0E7490', border: '1px solid #0E7490', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
          + Add stage ({stages.length}/5)
        </button>
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer', marginTop: 14 }}>
        <input type="checkbox" checked={requireCompletion} onChange={e => setRequireCompletion(e.target.checked)} />
        Require full approval before this item can be marked complete
      </label>

      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}
